/**
 * WebGL Renderer - Shader preset showcase
 *
 * Renders a fullscreen triangle grid using WebGL with swappable fragment shaders.
 * Each preset demonstrates a different visual effect on the triangle field.
 */

import { SHADER_PRESETS, SHADER_CATEGORIES, VERTEX_SHADER, buildFragmentSource } from './core/webgl-shaders';
import type { ShaderPreset } from './core/webgl-shaders';

// ─── WebGL Setup ─────────────────────────────────────────────────────────────

const canvas = document.getElementById('gl-canvas') as HTMLCanvasElement;
const gl = canvas.getContext('webgl', { antialias: true, alpha: false });
if (!gl) throw new Error('WebGL not supported');

// Fullscreen quad geometry
const quadBuffer = gl.createBuffer()!;
gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
  -1, -1, 1, -1, -1, 1,
  -1, 1, 1, -1, 1, 1,
]), gl.STATIC_DRAW);

// ─── Shader Compilation ──────────────────────────────────────────────────────

function compileShader(src: string, type: number): WebGLShader {
  const shader = gl!.createShader(type)!;
  gl!.shaderSource(shader, src);
  gl!.compileShader(shader);
  if (!gl!.getShaderParameter(shader, gl!.COMPILE_STATUS)) {
    const log = gl!.getShaderInfoLog(shader);
    gl!.deleteShader(shader);
    throw new Error(`Shader compile error: ${log}`);
  }
  return shader;
}

function createProgram(vertSrc: string, fragSrc: string): WebGLProgram {
  const vs = compileShader(vertSrc, gl!.VERTEX_SHADER);
  const fs = compileShader(fragSrc, gl!.FRAGMENT_SHADER);
  const prog = gl!.createProgram()!;
  gl!.attachShader(prog, vs);
  gl!.attachShader(prog, fs);
  gl!.linkProgram(prog);
  if (!gl!.getProgramParameter(prog, gl!.LINK_STATUS)) {
    const log = gl!.getProgramInfoLog(prog);
    gl!.deleteProgram(prog);
    throw new Error(`Program link error: ${log}`);
  }
  // Clean up individual shaders
  gl!.deleteShader(vs);
  gl!.deleteShader(fs);
  return prog;
}

// ─── State ───────────────────────────────────────────────────────────────────

interface ProgramEntry {
  program: WebGLProgram;
  locs: {
    a_position: number;
    u_time: WebGLUniformLocation | null;
    u_resolution: WebGLUniformLocation | null;
    u_mouse: WebGLUniformLocation | null;
    u_param: WebGLUniformLocation | null;
  };
}

const programs: Map<string, ProgramEntry> = new Map();
let activePresetIndex = 0;
let mouseX = 0.5;
let mouseY = 0.5;
let startTime = performance.now();
let isAutoPlay = false;
let autoPlayInterval: number | null = null;

function getOrCreateProgram(preset: ShaderPreset): ProgramEntry | null {
  const existing = programs.get(preset.name);
  if (existing) return existing;

  try {
    const fragSrc = buildFragmentSource(preset);
    const program = createProgram(VERTEX_SHADER, fragSrc);
    const entry: ProgramEntry = {
      program,
      locs: {
        a_position: gl!.getAttribLocation(program, 'a_position'),
        u_time: gl!.getUniformLocation(program, 'u_time'),
        u_resolution: gl!.getUniformLocation(program, 'u_resolution'),
        u_mouse: gl!.getUniformLocation(program, 'u_mouse'),
        u_param: gl!.getUniformLocation(program, 'u_param'),
      },
    };
    programs.set(preset.name, entry);
    return entry;
  } catch (e) {
    console.error(`Failed to compile "${preset.name}":`, e);
    return null;
  }
}

// Pre-compile all shaders
for (const preset of SHADER_PRESETS) {
  getOrCreateProgram(preset);
}

// ─── Resize ──────────────────────────────────────────────────────────────────

function resize() {
  const dpr = window.devicePixelRatio || 1;
  const w = canvas.clientWidth;
  const h = canvas.clientHeight;
  canvas.width = w * dpr;
  canvas.height = h * dpr;
  gl!.viewport(0, 0, canvas.width, canvas.height);
}

window.addEventListener('resize', resize);
resize();

// ─── Mouse ───────────────────────────────────────────────────────────────────

canvas.addEventListener('mousemove', (e) => {
  mouseX = e.offsetX / canvas.clientWidth;
  mouseY = 1.0 - e.offsetY / canvas.clientHeight;
});

// ─── Render Loop ─────────────────────────────────────────────────────────────

function render() {
  const preset = SHADER_PRESETS[activePresetIndex];
  const entry = getOrCreateProgram(preset);
  if (!entry) { requestAnimationFrame(render); return; }

  const time = (performance.now() - startTime) / 1000;

  gl!.useProgram(entry.program);

  // Bind quad
  gl!.bindBuffer(gl!.ARRAY_BUFFER, quadBuffer);
  gl!.enableVertexAttribArray(entry.locs.a_position);
  gl!.vertexAttribPointer(entry.locs.a_position, 2, gl!.FLOAT, false, 0, 0);

  // Uniforms
  if (entry.locs.u_time) gl!.uniform1f(entry.locs.u_time, time);
  if (entry.locs.u_resolution) gl!.uniform2f(entry.locs.u_resolution, canvas.width, canvas.height);
  if (entry.locs.u_mouse) gl!.uniform2f(entry.locs.u_mouse, mouseX, mouseY);
  if (entry.locs.u_param) gl!.uniform1f(entry.locs.u_param, 0.5);

  gl!.drawArrays(gl!.TRIANGLES, 0, 6);
  requestAnimationFrame(render);
}

requestAnimationFrame(render);

// ─── UI: Preset Buttons ─────────────────────────────────────────────────────

const presetGrid = document.getElementById('preset-grid')!;
const presetName = document.getElementById('preset-name')!;
const presetDesc = document.getElementById('preset-desc')!;
const presetCategory = document.getElementById('preset-category')!;
const autoPlayBtn = document.getElementById('auto-play')!;
const categoryBtns = document.querySelectorAll<HTMLButtonElement>('.cat-btn');

let activeCategory: string | null = null;

function getFilteredPresets(): ShaderPreset[] {
  if (!activeCategory) return SHADER_PRESETS;
  return SHADER_PRESETS.filter(p => p.category === activeCategory);
}

function renderPresetButtons() {
  presetGrid.innerHTML = '';
  const filtered = getFilteredPresets();
  filtered.forEach((preset) => {
    const globalIndex = SHADER_PRESETS.indexOf(preset);
    const btn = document.createElement('button');
    btn.className = 'preset-btn' + (globalIndex === activePresetIndex ? ' active' : '');
    btn.textContent = preset.name;
    btn.addEventListener('click', () => selectPreset(globalIndex));
    presetGrid.appendChild(btn);
  });
}

function selectPreset(index: number) {
  activePresetIndex = index;
  const preset = SHADER_PRESETS[index];
  presetName.textContent = preset.name;
  presetDesc.textContent = preset.description;
  presetCategory.textContent = preset.category;
  presetCategory.className = 'cat-badge cat-' + preset.category;
  renderPresetButtons();
}

// Category filter
categoryBtns.forEach(btn => {
  btn.addEventListener('click', () => {
    const cat = btn.dataset.cat || null;
    if (cat === activeCategory || cat === 'all') {
      activeCategory = null;
    } else {
      activeCategory = cat;
    }
    categoryBtns.forEach(b => b.classList.toggle('active', (b.dataset.cat || null) === (activeCategory || 'all')));
    renderPresetButtons();
  });
});

// Auto-play
function toggleAutoPlay() {
  isAutoPlay = !isAutoPlay;
  autoPlayBtn.classList.toggle('active', isAutoPlay);
  autoPlayBtn.textContent = isAutoPlay ? 'Stop' : 'Auto';
  if (isAutoPlay) {
    autoPlayInterval = window.setInterval(() => {
      selectPreset((activePresetIndex + 1) % SHADER_PRESETS.length);
    }, 5000);
  } else if (autoPlayInterval) {
    clearInterval(autoPlayInterval);
    autoPlayInterval = null;
  }
}

autoPlayBtn.addEventListener('click', toggleAutoPlay);

// Keyboard navigation
document.addEventListener('keydown', (e) => {
  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    e.preventDefault();
    selectPreset((activePresetIndex + 1) % SHADER_PRESETS.length);
  } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    e.preventDefault();
    selectPreset((activePresetIndex - 1 + SHADER_PRESETS.length) % SHADER_PRESETS.length);
  } else if (e.key === ' ') {
    e.preventDefault();
    toggleAutoPlay();
  } else if (e.key === 'Escape') {
    window.location.href = '/';
  }
});

// Initial render
selectPreset(0);
